import {notifyUser} from './feedback';
import {createRegisteredRecord,discardUntouchedCreation,type CreationResource} from '../../features/erp/creationRecords';
import type {ReactNode} from 'react';
import { useCallback,useEffect,useMemo,useRef,useState } from 'react';
import { domainsForCacheKey } from '../../api/dataDomains';
import { DataChange } from '../../api/dataSync';
import { chineseErrorMessage } from '../../api/errorMessage';
import { queryRevision,invalidateQueries,peekQuery,peekQueryState,putQuery,readQuery,readSessionKey } from '../../api/queryCache';
import { useAdjacentPrefetch,useCriticalRead } from './readiness';
import { isReadFresh,ReadPolicy,useDataPolling,useDataRefresh,useDocumentVisible } from './resources';
import { useRetainedRoute,useRouteActive } from './runtime';
import type { ListWindow } from '../primitives/content/List';

/** Explicit priority actions precede sharing in the first group after multi-select. */
export function prioritizeShareExport<T extends {label:string;separatorBefore?:boolean;beforeShareExport?:boolean}>(actions:readonly T[]):T[] {
    const priority=actions.filter(action=>action.beforeShareExport&&action.label!=='分享与打印');
    const share=actions.filter(action=>action.label==='分享与打印');
    if(!priority.length&&!share.length)return [...actions];
    const rest=actions.filter(action=>!action.beforeShareExport&&action.label!=='分享与打印');
    return [...[...priority,...share].map(action=>({...action,separatorBefore:false})),...rest.map((action,index)=>index===0?{...action,separatorBefore:true}:action)];
}

/** One action definition for a record menu and its selected-records menu. */
export type RecordSelectionAction<T> = Omit<import('../primitives/content/BaseList').InfoListItemAction,'onClick'> & {
    key:string;
    available?:(record:T)=>boolean;
    batch:true | {reason:string};
    run:(records:T[])=>unknown;
};
export function recordSelectionActions<T>(records:readonly T[],definitions:readonly RecordSelectionAction<T>[]):import('../primitives/content/BaseList').InfoListItemAction[] {
    if(!records.length)return [];
    const snapshot=[...records];
    return definitions.filter(action=>!action.disabled && !action.disabledReason
        && (snapshot.length===1 || action.batch===true)
        && snapshot.every(row=>!action.available || action.available(row)))
        .map(({available,batch,run,...action})=>({...action,onClick:()=>run(snapshot)}));
}

/** Explicit sequential work: acknowledge one successful item before advancing. */
export function useRecordQueue<T>() {
    const [items,setItems]=useState<T[]>([]);
    const ref=useRef(items);ref.current=items;
    const start=(rows:T[])=>{ref.current=[...rows];setItems(ref.current);};
    const advance=()=>{ref.current=ref.current.slice(1);setItems(ref.current);return ref.current[0];};
    return {current:items[0],remaining:items.length,start,advance,clear:()=>start([])};
}

/** Shared vocabulary. Backend field identity stays independent of the displayed dimension. */
export type SortDirection = 'asc' | 'desc';
export type CriterionKind = 'name' | 'code' | 'time' | 'added' | 'price' | 'quantity' | 'size' | 'number' | 'status';
export interface SortOption { key: string; label: string; dir?: SortDirection; group?: string; kind?: CriterionKind }
const dimensions: Record<string, {label: string; kind: CriterionKind}> = {
    name: {label:'名称',kind:'name'}, sku: {label:'SKU',kind:'code'},
    added: {label:'加入时间',kind:'added'}, created_at: {label:'创建时间',kind:'time'},
    title: {label:'主题',kind:'name'},
    session_time: {label:'场次时间',kind:'time'}, order_date: {label:'下单时间',kind:'time'},
    returned_at: {label:'回传时间',kind:'time'}, number_date: {label:'编号',kind:'time'},
    sale_price: {label:'售价',kind:'price'}, final_listing_price: {label:'售价',kind:'price'}, purchase_price: {label:'采购价',kind:'price'}, amount: {label:'金额',kind:'price'},
    diameter: {label:'口径',kind:'size'}, quantity: {label:'数量',kind:'quantity'},
    item_count: {label:'款式数量',kind:'quantity'}, reserved_quantity: {label:'安排数量',kind:'quantity'},
    sold_quantity: {label:'售出数量',kind:'quantity'}, available_quantity: {label:'可用数量',kind:'quantity'},
    incoming_quantity: {label:'待收货数量',kind:'quantity'}, target_quantity: {label:'数量',kind:'quantity'},
    purchased_quantity: {label:'采购数量',kind:'quantity'}, received_quantity: {label:'收货数量',kind:'quantity'},
    status: {label:'状态',kind:'status'},
};
const directions: Record<CriterionKind, readonly [string,string]> = {
    name:['A 到 Z','Z 到 A'], code:['A 到 Z','Z 到 A'],
    time:['最早的在前','最新的在前'], added:['最早加入的在前','新加入的在前'],
    price:['最低的在前','最高的在前'], quantity:['最少的在前','最多的在前'],
    size:['最小的在前','最大的在前'], number:['最小的在前','最大的在前'],
    status:['正序','倒序'],
};
export function sortLabel(key: string, dir: SortDirection, label?: string, kind?: CriterionKind): string {
    const known = dimensions[key];
    return `${(kind && label ? label : known?.label) ?? label ?? key} · ${directions[kind ?? known?.kind ?? 'number'][dir === 'asc' ? 0 : 1]}`;
}
/** All product lists share these price choices; amounts of orders/customers remain separate. */
export const PRODUCT_PRICE_SORT_OPTIONS:SortOption[]=([['sale_price','售价'],['purchase_price','采购价']] as const).flatMap(([key,label])=>(['desc','asc'] as const).map(dir=>({key,label,dir,kind:'price' as const})));
export function normalizeProductSort(sort:string):string {
    return sort.replace(/^price:/,'purchase_price:').replace(/^final_listing_price:/,'sale_price:');
}
export function expandSortOptions(fields: SortOption[]): (SortOption & {dir: SortDirection})[] {
    return fields.flatMap(field => (field.dir ? [field.dir] : ['asc','desc'] as const).map(dir => ({
        ...field, dir, label: field.group === (dimensions[field.key]?.label ?? field.label)
            ? directions[field.kind ?? dimensions[field.key]?.kind ?? 'number'][dir === 'asc' ? 0 : 1]
            : sortLabel(field.key, dir, field.label, field.kind),
    })));
}
/** A filter option is an equality choice. Other operators must be declared, never inferred from its label. */
export type FilterOperator = 'eq' | 'neq' | 'contains' | 'gte' | 'lte' | 'between' | 'empty' | 'notEmpty';
export function filterLabel(dimension: string, value: string, operator: FilterOperator = 'eq'): string {
    const operators: Record<FilterOperator,string> = {eq:'',neq:'不等于 ',contains:'包含 ',gte:'至少 ',lte:'至多 ',between:'范围 ',empty:'未填写',notEmpty:'已填写'};
    return `${dimension} · ${operators[operator]}${operator === 'empty' || operator === 'notEmpty' ? '' : value}`;
}

/** Comma-separated values preserve the existing saved string contract. Empty means unrestricted. */
export const filterValues = (value: string) => value ? value.split(',').filter(Boolean) : [];
export const matchesFilter = (value: string, candidate: string | number) => !value || filterValues(value).includes(String(candidate));
export const toggleFilterValue = (value: string, candidate: string) => {
    const values = filterValues(value);
    return (values.includes(candidate) ? values.filter(item => item !== candidate) : [...values, candidate]).join(',');
};
export interface ListFilterGroup {
    key: string;
    label: string;
    value: string;
    set: (value: string) => void;
    options: {value: string; label: string; disabled?: boolean}[];
    /** The same choice semantics apply in the title, quick tabs and menu. */
    selectionMode?: 'single' | 'multiple';
    control?: ReactNode;
    summary?: string;
    /** Preserve composite condition terms for common title counting and descriptions. */
    titleConditions?: {values: readonly string[]; operator: 'and' | 'or'};
    showTabs?: boolean;
    allowAll?: boolean;
    /** A scope tab may remain visible without duplicating it in the filter menu. */
    tabsOnly?: boolean;
    /** This dimension may supply a prefix already contained in the subject. */
    titlePrefix?: boolean;
    /** Only exhaustive enum domains may treat all selected values as unrestricted in the title. */
    allOptionsKnown?: boolean;
}

export interface FilterTitleGroup { label: string; values: readonly string[]; titlePrefix?: boolean; operator?: 'and' | 'or' }

export function selectListFilter(group: ListFilterGroup, value: string) {
    if (!value && group.allowAll === false) return;
    group.set(!value || group.selectionMode === 'single' ? value : toggleFilterValue(group.value,value));
}

/** Keep groups as renderable controls; their outline conveys OR grouping without punctuation. */
export function filteredListTitle(title: string, selectedGroups: readonly FilterTitleGroup[]) {
    const joinValues = (group: FilterTitleGroup) => group.values.join(group.operator==='and'?'且':'或');
    const selectedSummary = (group: FilterTitleGroup) => `${group.label || '条件'}·已选${group.values.length}项`;
    const selected = selectedGroups.map((group,index) => ({label:group.label,index,titlePrefix:group.titlePrefix,operator:group.operator,values:group.values.map(value=>value.trim()).filter(value=>value && value!=='全部')})).filter(group=>group.values.length);
    let subject = selected.length ? title.replace(/^全部/, '').trimStart() : title;
    const groups = selected.map(group=>({...group,text:group.values.length>2 || new Set(group.values).size<group.values.length ? selectedSummary(group) : joinValues(group)})).sort((a,b)=>Number(!!a.titlePrefix)-Number(!!b.titlePrefix));
    const prefix = groups.map(group=>group.text).join('且');
    const collapsed = groups.length>2 || Array.from(prefix).length>24;
    const summary = collapsed ? groups.length===1 ? selectedSummary(groups[0]) : `已筛选·${groups.length}组` : prefix;
    if (!collapsed && groups.length) {
        const last = groups[groups.length-1];
        if(last.titlePrefix && last.values.length===1) {
            if(subject.startsWith(last.text)) subject=subject.slice(last.text.length);
            else if(last.text.endsWith(subject)) subject='';
        }
    }
    const description = groups.map(group=>`${group.label}：${joinValues(group)}`).join('；且');
    return {subject,groups,collapsed,summary,text:`${summary}${subject}`,description};
}

// Keep drag gestures independent of network latency. Only persistence and
// background reconciliation wait; opening lists and loading scroll pages do not.
const idleDelay = 300;
let active = 0;
let touchedAt = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
const waiting = new Set<() => void>();
const gestureWaiting = new Set<() => void>();
function schedule() {
    clearTimeout(timer);
    if (active || !waiting.size) return;
    timer = setTimeout(() => {
        const callbacks = [...waiting]; waiting.clear();
        callbacks.forEach(resolve => resolve());
    }, Math.max(0, touchedAt + idleDelay - Date.now()));
}
export function touchListInteraction() { touchedAt = Date.now(); schedule(); }
export function beginListInteraction() {
    active++; clearTimeout(timer);
    let ended = false;
    return () => { if (!ended) { ended = true; active--; if (!active) { const callbacks=[...gestureWaiting]; gestureWaiting.clear(); callbacks.forEach(resolve=>resolve()); } touchListInteraction(); } };
}
/** Persistence needs the gesture to end, without the background refresh debounce. */
export function waitForListGestureEnd(): Promise<void> {
    return active ? new Promise(resolve => gestureWaiting.add(resolve)) : Promise.resolve();
}
export function isListInteractionPending() { return active > 0 || Date.now() < touchedAt + idleDelay; }
export function waitForListInteractionIdle(): Promise<void> {
    if (!isListInteractionPending()) return Promise.resolve();
    return new Promise(resolve => { waiting.add(resolve); schedule(); });
}

/** Suppress browser image ghosts globally; pointer-based app sorting remains intact. */
export function installImageDragGuard() {
    const preventImageDrag = (event: DragEvent) => {
        if (event.target instanceof Element && event.target.closest('img')) event.preventDefault();
    };
    document.addEventListener('dragstart', preventImageDrag, true);
    return () => document.removeEventListener('dragstart', preventImageDrag, true);
}

const getListCache = <T,>(key: string) => peekQuery<T>(`view|${key}`);
const setListCache = <T,>(key: string, value: T) => putQuery(`view|${key}`, value);

type Page<T> = { items: T[]; total: number; pages: number };
type State<T> = Page<T> & { pageData?: Page<T>; dataKey: string; blocks: Map<number, T[]>; page: number; loading: boolean; fetching: boolean; loadingMore: boolean; error: string | null };

/** Sparse absolute row positions: bounded data, stable scroll extent, bidirectional reads. */
export function usePagedList<T>(key: string,
    fetchPage: (page: number, signal: AbortSignal) => Promise<Page<T>>,
    identity: (item: T) => string | number,
    reconcile?: (items: T[], change: DataChange) => T[], pageSize = 20, prefetchPages = 0, deferRefresh?: () => boolean, resetOnChange?: (change: DataChange) => boolean, shouldRefresh?: (change: DataChange, items: T[]) => boolean, policy?: ReadPolicy, warmNextPage = true, retainLoadedPages = false) {
    const session=readSessionKey();
    const resourceIdentity=JSON.stringify([session,key]);
    const resourceRef=useRef(resourceIdentity);resourceRef.current=resourceIdentity;
    const mounted=useRef(true);
    useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
    const isCurrent=useCallback(()=>mounted.current && resourceRef.current===resourceIdentity && readSessionKey()===session,[resourceIdentity,session]);
    const shouldRefreshRef = useRef(shouldRefresh); shouldRefreshRef.current = shouldRefresh;
    const resetOnChangeRef = useRef(resetOnChange); resetOnChangeRef.current = resetOnChange;
    const [windowRevision, setWindowRevision] = useState(0);
    const deferRef = useRef(deferRefresh); deferRef.current = deferRefresh;
    const routeActive = useRouteActive();
    const documentVisible = useDocumentVisible();
    const visible = routeActive && documentVisible;
    const retained = useRetainedRoute();
    const visibleRef = useRef(visible); visibleRef.current = visible;
    const startedKey = useRef<string | null>(null);
    const refreshedAt = useRef(peekQueryState(`view|${key}`)?.updatedAt ?? 0);
    const [state, setState] = useState<State<T>>(() => {
        const cached = getListCache<Page<T>>(key);
        return { pageData: cached, dataKey: resourceIdentity, items: cached?.items ?? [], total: cached?.total ?? 0, pages: cached?.pages ?? 0,
            blocks: new Map(cached ? [[1, cached.items]] : []), page: 1,
            loading: !cached, fetching: false, loadingMore: false, error: null };
    });
    // A different search/filter identity must never expose the previous result, even before effects run.
    const scopedState: State<T> = state.dataKey === resourceIdentity ? state : {
        dataKey: resourceIdentity, items: [], total: 0, pages: 0, blocks: new Map(), page: 1,
        loading: true, fetching: false, loadingMore: false, error: null,
    };
    const current = useRef(state);
    const fetchRef = useRef(fetchPage); fetchRef.current = fetchPage;
    const reconcileRef = useRef(reconcile); reconcileRef.current = reconcile;
    const identityRef = useRef(identity); identityRef.current = identity;
    const active = useRef(new Map<number, AbortController>());
    const generation = useRef(0);
    const windowed = useRef(false);
    const viewport = useRef({ start: 0, stop: 0 });
    const wanted = useRef<number[]>([1]);
    const pumpRef = useRef<() => void>(() => {});
    const update = useCallback((next: State<T>) => { current.current = next; setState(next); }, []);
    const publishBlocks = useCallback((blocks: Map<number, T[]>, patch: Partial<State<T>> = {}) => {
        const ordered = [...blocks].sort(([a], [b]) => a - b);
        const items = Array.from(new Map(ordered.flatMap(([, rows]) => rows).map(item => [identityRef.current(item), item])).values());
        update({ ...current.current, ...patch, blocks, items, page: Math.max(1, ...blocks.keys()) });
    }, [update]);
    const cancel = useCallback(() => {
        generation.current++;
        active.current.forEach(controller => controller.abort());
        active.current.clear();
    }, []);
    const trim = useCallback((blocks: Map<number, T[]>) => {
        if (!windowed.current || retainLoadedPages) return blocks;
        const nearby = new Set(wanted.current.flatMap(page=>[page-2,page-1,page,page+1,page+2]));
        for (const page of Array.from(blocks.keys())) if (!nearby.has(page)) blocks.delete(page);
        return blocks;
    }, [retainLoadedPages]);

    const pump = useCallback(() => {
        if (!isCurrent() || deferRef.current?.() || ((retained || policy) && !visibleRef.current) || current.current.loading || current.current.dataKey !== resourceIdentity || (policy && active.current.has(0)) || current.current.error) return;
        for (const page of wanted.current) {
            if (active.current.size >= 2) break;
            if (current.current.blocks.has(page) || active.current.has(page) || page > current.current.pages) continue;
            const controller = new AbortController();
            const epoch = generation.current;
            active.current.set(page, controller);
            update({ ...current.current, loadingMore: true });
            void readQuery(`page|${key}|${page}`, signal=>fetchRef.current(page,signal), {signal:controller.signal}).then(result => {
                if (controller.signal.aborted || generation.current !== epoch || !isCurrent()) return;
                const blocks = trim(new Map(current.current.blocks).set(page, result.items));
                publishBlocks(blocks, { pageData: result, total: result.total, pages: result.pages });
            }).catch(error => {
                if (!controller.signal.aborted && generation.current === epoch && isCurrent() && (error as {name?:string})?.name !== 'AbortError') {
                    update({ ...current.current, error: chineseErrorMessage(error, '加载失败，请重试') });
                }
            }).finally(() => {
                if (generation.current !== epoch || active.current.get(page) !== controller || !isCurrent()) return;
                active.current.delete(page);
                update({ ...current.current, loadingMore: active.current.size > 0 });
                pumpRef.current();
            });
        }
    }, [publishBlocks, trim, update, retained, key, policy, resourceIdentity,isCurrent]);
    pumpRef.current = pump;

    const onRangeChange = useCallback((start: number, stop: number, ranges?: {start:number;stop:number}[]) => {
        if (!isCurrent() || (retained || policy) && !visibleRef.current) return;
        windowed.current = true;
        const previous = viewport.current;
        if (!ranges && start === previous.start && stop === previous.stop) { pumpRef.current(); return; }
        const moving = start !== previous.start;
        viewport.current = { start, stop };
        const first = Math.floor(start / pageSize) + 1;
        const last = Math.floor(stop / pageSize) + 1;
        const pages = ranges ? Array.from(new Set(ranges.flatMap(range => {
            const from=Math.floor(range.start/pageSize)+1, to=Math.floor(range.stop/pageSize)+1;
            return Array.from({length:Math.max(0,to-from+1)},(_,index)=>from+index);
        }))) : Array.from({ length: last - first + 1 }, (_, index) => first + index);
        // First paint reads only visible pages. Offscreen prefetch is opt-in, never a site default.
        if (moving) {
            const ahead = Math.min(Math.max(0, prefetchPages), Math.ceil((stop - start + 1) / pageSize));
            for (let step = 1; step <= ahead; step++) {
                pages.push(...(start >= previous.start ? [last + step, first - step] : [first - step, last + step]));
            }
        }
        wanted.current = pages.filter(page => page >= 1 && page <= current.current.pages);
        // Cancel abandoned jumps so visible rows never wait behind offscreen reads.
        active.current.forEach((controller, page) => {
            if (page !== 0 && !wanted.current.includes(page)) { controller.abort(); active.current.delete(page); }
        });
        const blocks = trim(new Map(current.current.blocks));
        if (blocks.size !== current.current.blocks.size || current.current.loadingMore !== (active.current.size > 0 && !current.current.loading))
            publishBlocks(blocks, { loadingMore: active.current.size > 0 && !current.current.loading });
        pumpRef.current();
    }, [pageSize, prefetchPages, publishBlocks, trim, retained, policy,isCurrent]);

    const refreshFlight=useRef<{key:string;revision:number;epoch:number;promise:Promise<void>}|null>(null);
    const refresh = useCallback((reset = false, quiet = false, allowCached = false):Promise<void> => {
        if(!isCurrent())return Promise.resolve();
        const revision=queryRevision(`view|${key}`);
        const flight=refreshFlight.current;
        if(!reset && flight?.key===resourceIdentity && flight.revision===revision && flight.epoch===generation.current && active.current.has(0))return flight.promise;
        const promise=(async()=>{
            if (deferRef.current?.()) return;
            if ((retained || policy) && !visibleRef.current) return;
            cancel();
            const epoch = generation.current;
            if (reset) { viewport.current = { start: 0, stop: 0 }; wanted.current = [1]; }
            const controller = new AbortController();
            active.current.set(0, controller);
            if (current.current.dataKey !== resourceIdentity) {
                publishBlocks(new Map(), { dataKey: resourceIdentity, pageData: undefined, total: 0, pages: 0, loading: true });
            }
            update({ ...current.current, loading: policy ? !current.current.pageData : quiet ? current.current.loading : true, fetching: true, loadingMore: false, error: null });
            try {
                // Grouped rows can project distant source pages into one viewport.
                // Refresh those pages, never the entire interval between them.
                const pages = reset ? [1] : windowed.current ? Array.from(new Set(wanted.current.length
                    ? wanted.current : [Math.floor(viewport.current.start / pageSize) + 1])).sort((a,b)=>a-b) : Array.from(new Set([
                    Math.floor(viewport.current.start / pageSize) + 1, ...current.current.blocks.keys(),
                ])).sort((a, b) => a - b);
                const blocks = new Map<number, T[]>();
                let metadata: Page<T> | undefined;
                for (let index = 0; index < pages.length; index += 2) {
                    const batch = await Promise.all(pages.slice(index, index + 2).map(async page => {
                        const result = await readQuery(`page|${key}|${page}`,signal=>fetchRef.current(page,signal),{signal:controller.signal,staleTime:allowCached ? policy?.staleTime ?? 30_000 : 0});
                        return { page, result };
                    }));
                    if (controller.signal.aborted || generation.current !== epoch || !isCurrent() || queryRevision(`view|${key}`)!==revision) return;
                    for (const { page, result } of batch) {
                        metadata = result;
                        if (page <= result.pages || page === 1) blocks.set(page, result.items);
                        if (page === 1) setListCache(key, result);
                    }
                }
                if (metadata) refreshedAt.current = Date.now();
                if (metadata) publishBlocks(blocks, { pageData: metadata, dataKey: resourceIdentity, total: metadata.total, pages: metadata.pages, loading: false, fetching: false, error: null });
            } catch (error) {
                if (!controller.signal.aborted && generation.current === epoch && isCurrent()) update({ ...current.current, loading: false, fetching: false,
                    error: (error as {name?:string})?.name === 'AbortError' ? null : chineseErrorMessage(error, '加载失败，请重试') });
                // A rejected page ends the batch; release any sibling read immediately.
                controller.abort();
            } finally {
                if (generation.current === epoch && isCurrent()) { active.current.delete(0); pumpRef.current(); }
            }
        })();
        const currentFlight={key:resourceIdentity,revision,epoch:generation.current,promise};refreshFlight.current=currentFlight;
        void promise.finally(()=>{if(refreshFlight.current===currentFlight)refreshFlight.current=null;}).catch(()=>{});
        return promise;
    }, [cancel, key, pageSize, publishBlocks, update, retained, policy,resourceIdentity,isCurrent]);

    useEffect(() => {
        if (visible) {
            const changed = startedKey.current !== resourceIdentity;
            startedKey.current = resourceIdentity;
            if (policy && changed) {
                deferredChange.current = null;
                viewport.current = { start: 0, stop: 0 }; wanted.current = [1];
                const snapshot = peekQueryState<Page<T>>(`view|${key}`);
                if (snapshot) {
                    refreshedAt.current = snapshot.updatedAt;
                    publishBlocks(new Map([[1, snapshot.value.items]]), { pageData: snapshot.value, dataKey: resourceIdentity,
                        total: snapshot.value.total, pages: snapshot.value.pages, loading: false, fetching: false, loadingMore: false, error: null });
                    if (isReadFresh(snapshot.updatedAt, policy)) return cancel;
                }
                void refresh(true, true, true);
            } else if (changed) {
                if (retained) deferredChange.current = null;
                void refresh(true, false, true);
            } else if (!deferredChange.current) {
                if (current.current.loading || current.current.fetching || current.current.dataKey !== resourceIdentity || !isReadFresh(refreshedAt.current, policy)) void refresh(false, true);
                else pumpRef.current();
            }
        }
        return cancel;
    }, [refresh, cancel, visible, key, retained, policy, publishBlocks,resourceIdentity]);
    const refreshLoaded = useCallback((options?: {quiet?: boolean; invalidate?: boolean}) => {
        if(!isCurrent())return Promise.resolve();
        const flight=refreshFlight.current;
        if(flight?.key===resourceIdentity && flight.revision===queryRevision(`view|${key}`) && flight.epoch===generation.current && active.current.has(0))return flight.promise;
        if (options?.invalidate !== false) invalidateQueries(query => query===`view|${key}` || query.startsWith(`page|${key}|`));
        return refresh(false, options?.quiet);
    }, [key, refresh,resourceIdentity,isCurrent]);
    const setItems = useCallback((change: (items: T[]) => T[]) => {
        if(!isCurrent())return;
        const previous = current.current;
        const items = Array.from(new Map(change(previous.items).map(item => [identityRef.current(item), item])).values());
        const byId = new Map(items.map(item => [identityRef.current(item), item]));
        const blocks = new Map<number, T[]>(Array.from(previous.blocks, ([page, rows]): [number, T[]] => [page,
            rows.flatMap(item => { const next = byId.get(identityRef.current(item)); return next === undefined ? [] : [next]; })]));
        const existing = new Set(previous.items.map(item => identityRef.current(item)));
        const added = items.filter(item => !existing.has(identityRef.current(item)));
        if (added.length && blocks.has(1)) blocks.set(1, [...added, ...blocks.get(1)!].slice(0, pageSize));
        publishBlocks(blocks, { total: Math.max(0, previous.total + items.length - previous.items.length) });
    }, [pageSize, publishBlocks,isCurrent]);
    const changeRevision = useRef(0);
    useEffect(()=>()=>{changeRevision.current++;},[resourceIdentity]);
    const deferredChange = useDataRefresh(domainsForCacheKey(key), async change => {
        if(!isCurrent())return;
        const revision = ++changeRevision.current;
        if (isListInteractionPending()) {
            await waitForListInteractionIdle();
            if (revision !== changeRevision.current || !isCurrent()) return;
        }
        if (!isCurrent() || deferRef.current?.()) return;
        if (change.reason === 'focus' && (active.current.size || isReadFresh(refreshedAt.current, policy))) return;
        const needsRead = active.current.size > 0 || (shouldRefreshRef.current?.(change, current.current.items) ?? true);
        if (reconcileRef.current) setItems(items => reconcileRef.current!(items, change));
        if (!needsRead) {
            // A reconciled current projection is fresh; other queries stay invalidated.
            refreshedAt.current = Date.now();
            if (policy && current.current.blocks.has(1)) setListCache(key, { ...current.current.pageData, items: current.current.blocks.get(1)!, total: current.current.total, pages: current.current.pages });
            return;
        }
        const reset = resetOnChangeRef.current?.(change) ?? false;
        if (reset) setWindowRevision(value => value + 1);
        return refresh(reset, !!policy || change.reason === 'focus');
    }, visible, true, `view|${key}`, true);
    useDataPolling(() => {
        if (!policy || current.current.error || active.current.size || isReadFresh(refreshedAt.current, policy)) return;
        return refresh(false, true);
    }, policy?.revalidateInterval ?? 30_000, !!policy && visible);
    const loadMore = useCallback(() => {
        if (!isCurrent() || current.current.loading || current.current.error || active.current.size) return;
        wanted.current = [current.current.page + 1];
        pumpRef.current();
    }, [isCurrent]);
    const setPageData = useCallback((change: (page: Page<T> | undefined) => Page<T> | undefined) => {if(isCurrent())update({...current.current, pageData:change(current.current.pageData)});}, [update,isCurrent]);
    const cancelReads=useCallback(()=>{if(isCurrent())cancel();},[cancel,isCurrent]);
    useCriticalRead(visible && !scopedState.pageData && !scopedState.error);
    const nextPage=Math.floor(viewport.current.stop/pageSize)+2;
    useAdjacentPrefetch(warmNextPage && visible && !state.loading && !state.fetching && !state.loadingMore && !state.error && state.dataKey===resourceIdentity,
        nextPage<=state.pages && !state.blocks.has(nextPage) ? [{key:`list-next|${key}|${nextPage}`,read:async signal=>{
            // Transport cache warms only. Offscreen metadata cannot replace the current visible page.
            await readQuery(`page|${key}|${nextPage}`, innerSignal=>fetchRef.current(nextPage,innerSignal), {signal});
        }}] : []);
    const exportSource:ListExportSource<T>={revision:generation.current,total:scopedState.total,pageSize,readPage:fetchPage,ready:scopedState.dataKey===resourceIdentity&&!scopedState.loading&&!scopedState.fetching&&!scopedState.error,
        loaded:Array.from(scopedState.blocks.entries()).flatMap(([page,rows])=>rows.map((item,offset)=>({index:(page-1)*pageSize+offset,item})))};
    return { ...scopedState, dataKey:key, setItems, setPageData, cancelReads, hasMore: scopedState.page < scopedState.pages && !scopedState.loading && !scopedState.error,
        loadMore, refresh: refreshLoaded,
        exportSource,
        window: { selectionSource:exportSource, itemCount: scopedState.total, resetKey: resetOnChange ? `${key}|reset:${windowRevision}` : key, onRangeChange,
            getLoadedEntries: () => Array.from(scopedState.blocks.entries()).sort(([a],[b])=>a-b).flatMap(([page,rows])=>rows.map((item,offset)=>({index:(page-1)*pageSize+offset,item}))),
            getItem: (index: number) => scopedState.blocks.get(Math.floor(index / pageSize) + 1)?.[index % pageSize] } };
}

/** Preserve selected records across virtual pages; a failed batch keeps unprocessed rows. */
export function useListSelection<T extends {id:string|number}>(rows:readonly T[],scope?:string|number) {
    const [saved,setSaved]=useState(new Map<string,T>());
    const currentRows=useMemo(()=>new Map(rows.map(row=>[String(row.id),row])),[rows]);
    const selectionRevision=useRef(0);
    const request=useRef<AbortController|null>(null);
    const cancelSelectAll=useCallback(()=>{selectionRevision.current++;request.current?.abort();request.current=null;},[]);
    useEffect(()=>{setSaved(new Map());cancelSelectAll();return cancelSelectAll;},[scope,cancelSelectAll]);
    const selectedKeys=useMemo(()=>new Set(saved.keys()),[saved]);
    const records=useMemo(()=>Array.from(saved,([key,row])=>currentRows.get(key)??row),[saved,currentRows]);
    const onChange=(keys:Set<string>)=>{cancelSelectAll();setSaved(previous=>new Map(Array.from(keys).flatMap(key=>{
        const row=currentRows.get(key)??previous.get(key);
        return row?[[key,row] as const]:[];
    })));};
    const refreshRecords=()=>setSaved(previous=>new Map(Array.from(previous,([key,row])=>[key,currentRows.get(key)??row])));
    const onToggle=(key:string)=>{cancelSelectAll();setSaved(previous=>{
        const next=new Map(previous);
        if(next.has(key))next.delete(key);else{const row=currentRows.get(key);if(row)next.set(key,row);}
        return next;
    });};
    const removeMany=useCallback((ids:readonly (string|number)[])=>{cancelSelectAll();setSaved(previous=>{
        const next=new Map(previous);ids.forEach(id=>next.delete(String(id)));return next;
    });},[cancelSelectAll]);
    const remove=useCallback((id:string|number)=>removeMany([id]),[removeMany]);
    const selectAll=async(loadPage:(page:number,signal:AbortSignal)=>Promise<{items:T[];pages:number}>)=>{
        cancelSelectAll();
        const revision=selectionRevision.current;
        const controller=new AbortController();request.current=controller;
        const selected=new Map<string,T>();
        try {
            const first=await loadPage(1,controller.signal);
            if(controller.signal.aborted||revision!==selectionRevision.current)return false;
            first.items.forEach(row=>selected.set(String(row.id),row));
            const remaining=await readListPages(first.pages,loadPage,controller.signal,2);
            if(controller.signal.aborted||revision!==selectionRevision.current)return false;
            remaining.forEach(result=>result.items.forEach(row=>selected.set(String(row.id),row)));
            setSaved(previous=>revision===selectionRevision.current?(()=>{const next=new Map(previous);selected.forEach((row,key)=>next.set(key,row));return next;})():previous);
            return true;
        } catch(error) {
            if(controller.signal.aborted)return false;
            controller.abort(error);
            throw error;
        } finally {if(request.current===controller)request.current=null;}
    };
    return {selectedKeys,onChange,onToggle,refreshRecords,remove,removeMany,selectAll,cancelSelectAll,clear:()=>onChange(new Set()),records};
}

export type ListExportSource<T> = {revision?:number;total:number;pageSize:number;ready?:boolean;loaded:readonly {index:number;item:T}[];readPage:(page:number,signal:AbortSignal)=>Promise<{items:T[];total?:number}>};
export type ListExportSelection<T> = {total:number;expression:string;single:boolean;rows:T[];read:(positions:readonly number[])=>Promise<T[]>};

export function parseListPositions(expression:string,total:number):number[] {
    const positions=new Set<number>();
    for(const part of expression.replace(/[，、]/g,',').replace(/[–—~～至]/g,'-').split(',')){
        const match=part.trim().match(/^(\d+)\s*(?:-\s*(\d+))?$/);
        if(!match)throw new Error(`请输入 1-${total} 内的序号，逗号分隔各项，连字符连接区间`);
        const start=Number(match[1]),end=Number(match[2]??match[1]);
        if(!Number.isSafeInteger(start)||!Number.isSafeInteger(end)||start<1||end>total||start>end)throw new Error(`请输入 1-${total} 内从小到大的序号或区间`);
        for(let value=start;value<=end;value++)positions.add(value);
    }
    return [...positions].sort((a,b)=>a-b);
}
export function formatListPositions(positions:readonly number[]):string {
    const sorted=[...new Set(positions)].sort((a,b)=>a-b),parts:string[]=[];
    for(let i=0;i<sorted.length;i++){
        const start=sorted[i];while(i+1<sorted.length&&sorted[i+1]===sorted[i]+1)i++;
        parts.push(start===sorted[i]?String(start):`${start}-${sorted[i]}`);
    }
    return parts.join(',');
}

/** Positions belong to the full filtered list. Read only missing pages and never clear its query. */
export async function prepareListExport<T extends {id:string|number}>(selected:readonly T[],source:ListExportSource<T>,signal:AbortSignal):Promise<ListExportSelection<T>> {
    if(source.ready===false)throw new Error('列表正在更新，请更新完成后重新选择');
    const {total,pageSize}=source,cache=new Map(source.loaded.map(entry=>[entry.index,entry.item]));
    const check=()=>{if(signal.aborted)throw new Error('已取消');};
    const page=async(number:number)=>{
        check();const result=await source.readPage(number,signal);check();
        if(result.total!=null&&result.total!==total)throw new Error('列表数量已变化，请重新选择打印范围');
        result.items.forEach((item,offset)=>cache.set((number-1)*pageSize+offset,item));
    };
    const ids=new Set(selected.map(row=>row.id));
    const selectedEntries=()=>[...cache].filter(([index,item])=>index<total&&ids.has(item.id)).sort(([a],[b])=>a-b);
    for(let number=1;selectedEntries().length<ids.size&&number<=Math.ceil(total/pageSize);number++){
        const start=(number-1)*pageSize,end=Math.min(total,start+pageSize);
        if(Array.from({length:end-start},(_,offset)=>cache.has(start+offset)).every(Boolean))continue;
        await page(number);
    }
    check();const entries=selectedEntries();
    if(entries.length!==ids.size)throw new Error('部分所选花盆不在当前列表中，请重新选择');
    return {total,expression:formatListPositions(entries.map(([index])=>index+1)),single:ids.size===1,rows:entries.map(([,item])=>item),
        read:async positions=>{
            check();const sorted=[...new Set(positions)].sort((a,b)=>a-b);
            if(sorted.some(value=>!Number.isInteger(value)||value<1||value>total))throw new Error('打印序号超出当前列表范围');
            const pages=[...new Set(sorted.filter(value=>!cache.has(value-1)).map(value=>Math.floor((value-1)/pageSize)+1))];
            for(const number of pages)await page(number);
            check();const rows=sorted.map(value=>cache.get(value-1));
            if(rows.some(row=>!row)||new Set(rows.map(row=>row!.id)).size!==rows.length)throw new Error('列表内容已变化，请重新选择打印范围');
            return rows as T[];
        }};
}

/** One row action everywhere: same identity closes; another identity switches. */
export function nextListDetail<T>(current: T | null, next: T, same: (a:T,b:T)=>boolean, behavior: 'open'|'toggle' = 'toggle'): T | null {
    return behavior === 'toggle' && current !== null && same(current,next) ? null : next;
}
export function useListDetail<T>(getKey: (item:T)=>string | number) {
    const [selected,setSelected] = useState<T|null>(null);
    const select = (item:T) => setSelected(current=>nextListDetail(current,item,(a,b)=>getKey(a)===getKey(b)));
    return {selected,setSelected,select,close:()=>setSelected(null)};
}

/** Query changes with the input. Normalization deduplicates equivalent searches; reads own cancellation. */
export function useListSearch(initial = '') {
    const [search, setSearch] = useState(initial);
    const reset = useCallback((value = '') => setSearch(value), []);
    return { search, setSearch, query: normalizeSearch(search), reset };
}

/** Search copy and literal matching shared by ERP, logistics and local selectors. */
const POT_SEARCH_FIELDS = ['花盆名称','SKU','供应商','品牌或其他信息'] as const;
export const SEARCH_FIELDS = {
    live: ['直播主题','编号','人员','仓库或内容'],
    purchase: ['采购主题','编号','供应商','仓库','快递单号或内容'],
    product: POT_SEARCH_FIELDS,
    publicProduct: ['花盆名称','SKU','品牌或属性'],
    liveProduct: POT_SEARCH_FIELDS,
    purchaseProduct: POT_SEARCH_FIELDS,
    pool: POT_SEARCH_FIELDS,
    picker: POT_SEARCH_FIELDS,
    supplier: ['供应商','品牌','联系人','联系方式或其他信息'],
    order: ['订单号','用户名','邮箱','收件人','地址','备注','直播系列或花盆 SKU'],
    returnOrder: ['订单号','买家','原始花盆标题或 SKU'],
    hosts: ['姓名','电话','邮箱'],
    warehouses: ['名称','地址','联系电话'],
    categories: ['种类名称','备注'],
    attributes: ['属性名称','选项或备注'],
    attributeOptions: ['选项名称'],
    settings: ['设置名称'],
    groups: ['分组名称'],
    records: ['标题','副标题'],
} as const;
export type SearchProfile = keyof typeof SEARCH_FIELDS;
export const searchHint = (profile: SearchProfile) => `搜索${profile === 'order' ? ' ' : ''}${SEARCH_FIELDS[profile].join('、')}`;
export function normalizeSearch(value: string): string { return value.trim().replace(/\s+/g,' ').toLowerCase(); }
export function matchesSearch(query: string, ...fields: unknown[]): boolean {
    const flatten = (value: unknown): string => Array.isArray(value) ? value.map(flatten).join(' ') : value == null ? '' : String(value);
    const text = fields.map(flatten).join(' ').toLowerCase();
    return normalizeSearch(query).split(' ').filter(Boolean).every(term=>text.includes(term));
}

export interface SearchField<T> { label: string; value: (item: T) => unknown }
export const searchFieldsHint = <T,>(fields: readonly SearchField<T>[]) => `搜索${fields.map(field=>field.label).join('、')}`;

type RecordId = number|string;
type Source<T> = {items:T[];window?:ListWindow<T>};
const sameId = (left:RecordId,right:RecordId) => String(left) === String(right);

/** A local creation receipt sits above the server window without changing its cache or order. */
export function projectCreatedRecord<T extends {id:RecordId}>(source:Source<T>,created:T|null):Source<T> {
    if (!created) return source;
    const original = source.window;
    const entries = original ? original.getLoadedEntries?.() ?? (source.items.length === original.itemCount ? source.items.map((item,index)=>({index,item})) : []) : [];
    // Pin the position only. Once the server's list has this row, show its values: lists merge
    // confirmed saves into their rows, and refreshes bring summaries (counts, stock, warehouse).
    const loaded = source.items.find(item=>sameId(item.id,created.id)) ?? entries.find(entry=>sameId(entry.item.id,created.id))?.item;
    const record:T = loaded ? {...created,...loaded} : created;
    const items = [record,...source.items.filter(item=>!sameId(item.id,record.id))];
    if (!original) return {items};
    const originalIndex = entries.find(entry=>sameId(entry.item.id,record.id))?.index;
    const itemCount = original.itemCount + (originalIndex === undefined ? 1 : 0);
    const sourceIndex = (index:number) => index - 1 + (originalIndex !== undefined && index - 1 >= originalIndex ? 1 : 0);
    return {items,window:{
        selectionSource:original.selectionSource,
        selectionExtras:[...(original.selectionExtras??[]),record],
        itemCount,
        resetKey:`${original.resetKey}|created:${record.id}`,
        getItem:index=>index === 0 ? record : original.getItem(sourceIndex(index)),
        getLoadedEntries:()=>[{index:0,item:record},...entries.filter(entry=>!sameId(entry.item.id,record.id)).map(entry=>({
            index:entry.index + 1 - (originalIndex !== undefined && entry.index > originalIndex ? 1 : 0),item:entry.item,
        }))],
        onRangeChange:(start,stop,ranges)=>{
            const mapped = (ranges ?? [{start,stop}]).flatMap(range=>{
                const first = Math.max(1,range.start);
                const last = Math.min(itemCount - 1,range.stop);
                if (first > last) return [];
                const from = sourceIndex(first),to = sourceIndex(last);
                if (originalIndex !== undefined && from < originalIndex && to > originalIndex) {
                    return [{start:from,stop:originalIndex - 1},{start:originalIndex + 1,stop:to}];
                }
                return [{start:from,stop:to}];
            });
            if (mapped.length) original.onRangeChange(Math.min(...mapped.map(range=>range.start)),Math.max(...mapped.map(range=>range.stop)),mapped);
        },
    }};
}

/** Only the current creation is temporary; revisiting a record never pins it again. */
export function useCreatedListRecord<T extends {id:RecordId}>(created:T|undefined,editingId:RecordId|null|undefined,queryKey:string,enabled=true) {
    const active = useRouteActive();
    const seen = useRef(new Set<string>());
    const [current,setCurrent] = useState<{record:T;queryKey:string}|null>(null);
    useEffect(()=>{
        if (!active || !enabled || editingId == null) {
            setCurrent(null);
            return;
        }
        if (created && sameId(created.id,editingId) && !seen.current.has(String(created.id))) {
            seen.current.add(String(created.id));
            setCurrent({record:created,queryKey});
            return;
        }
        setCurrent(previous=>previous && previous.queryKey === queryKey && sameId(previous.record.id,editingId) ? previous : null);
    },[created,editingId,queryKey,active,enabled]);
    const update = useCallback((patch:Partial<T>&{id:RecordId})=>{
        setCurrent(previous=>previous && sameId(previous.record.id,patch.id) ? {...previous,record:{...previous.record,...patch}} : previous);
    },[]);
    const clear = useCallback((id?:RecordId)=>{
        setCurrent(previous=>previous && (id === undefined || sameId(previous.record.id,id)) ? null : previous);
    },[]);
    const record = active && enabled && editingId != null && current?.queryKey === queryKey && sameId(current.record.id,editingId) ? current.record : null;
    return {record,update,clear,project:(source:Source<T>)=>projectCreatedRecord(source,record)};
}

/** One click lifecycle: deduplicate, register, expose the pending request, clean up a rejected open. */
export function useListCreation() {
    const busy = useRef(false);
    const [creating, setCreating] = useState(false);
    const create = async <T extends {_creation_token?:string}>(options: {
        resource:CreationResource; request:()=>Promise<T>; identify:(row:T)=>number;
        open:(pending:Promise<T>)=>boolean|void|Promise<boolean|void>; error:string;
    }) => {
        if (busy.current) return false;
        busy.current=true; setCreating(true);
        const request=createRegisteredRecord(options.resource,options.request,options.identify);
        // Both consumers are attached immediately, including a host that rejects opening.
        void request.catch(()=>{});
        let openSettled = false;
        try {
            const opened=await options.open(request);
            openSettled = true;
            const row=await request;
            if(opened===false) await discardUntouchedCreation(options.resource,options.identify(row));
            return opened;
        } catch(error) {
            if (!openSettled) {
                // The server may finish after the host failed to open. Keep its receipt
                // and use the normal untouched-record guard, never an unconditional delete.
                try {
                    const row = await request;
                    await discardUntouchedCreation(options.resource, options.identify(row));
                } catch { /* Preserve the original error; the registry keeps failed cleanup retryable. */ }
            }
            notifyUser(chineseErrorMessage(error,options.error),'error');
            return false;
        }
        finally {busy.current=false;setCreating(false);}
    };
    return {create,creating};
}

/** Reuse complete resident pages; missing pages retain the exact query and cancellation signal. */
export function useCompleteListSelection<T>({source,selectAll,eligible,scope,onRead,disabled=false,getKey,extraRows=[],selectedKeys}:{source:ListExportSource<T>;selectAll:(read:(page:number,signal:AbortSignal)=>Promise<{items:T[];pages:number}>)=>Promise<unknown>;eligible:(row:T)=>boolean;scope:string;onRead?:(row:T)=>void;disabled?:boolean;getKey:(row:T,index:number)=>string;extraRows?:readonly T[];selectedKeys?:ReadonlySet<string>}) {
    const [busy,setBusy]=useState(false);
    const request=useRef<AbortController|null>(null);
    useEffect(()=>{setBusy(false);return()=>{request.current?.abort();request.current=null;};},[scope,source.revision]);
    const residentCache=useRef({scope,revision:source.revision,total:source.total,rows:new Map<number,T>(),selected:new Map<number,T>()});
    if(residentCache.current.scope!==scope || residentCache.current.total!==source.total || residentCache.current.revision!==source.revision)residentCache.current={scope,revision:source.revision,total:source.total,rows:new Map(),selected:new Map()};
    const cached=residentCache.current.rows;
    const pinned=residentCache.current.selected;
    if(source.loaded.some(({index,item})=>{const before=cached.get(index)??pinned.get(index);return before!==undefined && getKey(before,index)!==getKey(item,index);})){cached.clear();pinned.clear();}
    // Browsing must not build a second unbounded copy of every visited page.
    // Only explicit complete-selection reads persist here; viewport rows expire
    // with the paged source. Keep retained selections current when revisited.
    const resident=new Map(source.loaded.map(({index,item})=>[index,item]));
    for(const [index,row] of pinned)if(!selectedKeys?.has(getKey(row,index)))pinned.delete(index);
    if(source.ready!==false)source.loaded.forEach(({index,item})=>{if(cached.has(index))cached.set(index,item);else if(selectedKeys?.has(getKey(item,index)))pinned.set(index,item);});
    const cachedRow=(index:number)=>resident.get(index) ?? cached.get(index) ?? pinned.get(index);
    const choose=async(positions?:readonly number[])=>{
        if(disabled||request.current||source.ready===false)return false;
        const controller=new AbortController();request.current=controller;setBusy(true);
        const wanted=positions ? new Set(positions) : undefined;
        const requestCache=new Map([...cached,...pinned,...resident]);
        const pages=wanted ? [...new Set([...wanted].map(index=>Math.floor(index/source.pageSize)+1))].sort((a,b)=>a-b)
            : Array.from({length:Math.ceil(source.total/source.pageSize)},(_,index)=>index+1);
        const seen=new Set<string>();
        const cancel=()=>{controller.abort();if(request.current===controller){request.current=null;setBusy(false);}};
        let owner:AbortSignal|undefined;
        try{return await selectAll(async(page,signal)=>{
            if(owner!==signal){owner=signal;signal.addEventListener('abort',cancel,{once:true});}
            if(signal.aborted||controller.signal.aborted)throw new Error('选择已取消');
            const sourcePage=pages[page-1];
            if(sourcePage===undefined)return {items:!wanted?extraRows.filter(eligible):[],pages:0};
            const start=(sourcePage-1)*source.pageSize,end=Math.min(source.total,start+source.pageSize);
            const resident=Array.from({length:Math.max(0,end-start)},(_,offset)=>requestCache.get(start+offset));
            const result=resident.every(row=>row!==undefined)?{items:resident as T[],total:source.total}:await source.readPage(sourcePage,controller.signal);
            if(controller.signal.aborted)throw new Error('选择已取消');
            if((result.total!=null&&result.total!==source.total)||result.items.length!==end-start)throw new Error('列表数量已变化，请重新选择');
            for(const [offset,row] of result.items.entries()){const key=getKey(row,start+offset);if(seen.has(key))throw new Error('列表内容已变化，请重新选择');seen.add(key);}
            result.items.forEach((row,offset)=>{cached.set(start+offset,row);pinned.delete(start+offset);});
            const items=result.items.filter((row,offset)=>(!wanted || wanted.has(start+offset)) && eligible(row));items.forEach(row=>onRead?.(row));
            if(page===1&&!wanted)for(const row of extraRows){getKey(row,-1);if(eligible(row))items.push(row);}
            return {items,pages:pages.length};
        });}catch(error){if(!controller.signal.aborted || (owner?.reason===error && (error as {name?:string})?.name!=='AbortError' && (error as Error)?.message!=='选择已取消'))notifyUser(chineseErrorMessage(error,'未完成全选，请重新选择'));return false;}
        finally{controller.abort();owner?.removeEventListener('abort',cancel);if(request.current===controller){request.current=null;setBusy(false);}}
    };
    return {busy,cancel:()=>{request.current?.abort();request.current=null;setBusy(false);},onSelectAll:()=>choose(),selectPositions:choose,groupSelection:(positions:readonly number[],selected:ReadonlySet<string>,remove:(ids:readonly string[])=>void)=>{
        const keys=positions.flatMap(index=>{const row=cachedRow(index);return row && eligible(row)?[getKey(row,index)]:[];});
        const complete=positions.every(index=>resident.has(index)||cached.has(index)||pinned.has(index));
        const chosen=keys.filter(key=>selected.has(key)).length;
        const checked=complete && keys.length>0 && chosen===keys.length;
        return {checked,partial:chosen>0 && !checked,disabled:disabled || busy || source.ready===false || positions.length===0,
            toggle:()=>checked ? remove(keys) : choose(positions)};
    }};
}

export async function readListPages<T>(pages:number,read:(page:number,signal:AbortSignal)=>Promise<T>,signal:AbortSignal,start=1):Promise<T[]> {
    const results:T[]=[];
    let next=start;
    await Promise.all(Array.from({length:Math.min(4,Math.max(0,pages-start+1))},async()=>{
        while(next<=pages && !signal.aborted){const page=next++;results[page-start]=await read(page,signal);}
    }));
    return results;
}
